import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative overflow-hidden">
        <img src="https://images.pexels.com/photos/7611541/pexels-photo-7611541.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#007AFF]/85 to-emerald-600/70" />
        <div className="relative p-12 h-full flex flex-col justify-between text-white">
          <Link to="/" className="flex items-center gap-2 w-fit">
            <div className="w-9 h-9 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center"><Sparkles className="w-5 h-5" strokeWidth={2.5} /></div>
            <span className="font-display font-bold text-lg">CoachIQ</span>
          </Link>
          <div>
            <div className="font-display text-3xl font-bold leading-tight">Your assistant coach<br/>is waiting on the sideline.</div>
            <div className="mt-3 text-white/80 max-w-sm">Sign in to record games, build practices, and get answers written in plain English.</div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={submit} className="w-full max-w-sm animate-slide-up" data-testid="login-form">
          <div className="mb-8">
            <div className="font-display text-3xl font-bold tracking-tight">Welcome back</div>
            <div className="text-gray-500 mt-1">Sign in to continue</div>
            <div className="mt-2 text-xs text-gray-400" data-testid="login-uphaus-tag">CoachIQ · Built by <a href="https://uphauslabs.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#007AFF] font-semibold">UPHAUS Labs</a></div>
          </div>

          <label className="text-sm font-medium text-gray-700">Email</label>
          <input data-testid="login-email" required type="email" value={email} onChange={e=>setEmail(e.target.value)}
            className="mt-1.5 mb-4 w-full h-11 px-4 rounded-2xl border border-gray-200 focus:border-[#007AFF] focus:ring-2 focus:ring-[#007AFF]/20 outline-none transition-colors bg-white" />

          <label className="text-sm font-medium text-gray-700">Password</label>
          <input data-testid="login-password" required type="password" value={password} onChange={e=>setPassword(e.target.value)}
            className="mt-1.5 mb-6 w-full h-11 px-4 rounded-2xl border border-gray-200 focus:border-[#007AFF] focus:ring-2 focus:ring-[#007AFF]/20 outline-none transition-colors bg-white" />

          <button data-testid="login-submit" disabled={busy} type="submit" className="w-full h-11 rounded-full bg-[#007AFF] hover:bg-[#0056B3] disabled:opacity-50 text-white font-semibold transition-colors shadow-lg shadow-blue-500/20">
            {busy ? "Signing in…" : "Sign in"}
          </button>

          <div className="my-4 flex items-center gap-3 text-xs text-gray-400">
            <div className="flex-1 h-px bg-gray-200" /> or <div className="flex-1 h-px bg-gray-200" />
          </div>

          <GoogleSignInButton label="Continue with Google" testid="login-google-btn" />

          <div className="mt-6 text-sm text-gray-500 text-center">
            New here? <Link to="/signup" className="text-[#007AFF] font-semibold hover:underline" data-testid="login-goto-signup">Create an account</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
